import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';

import {
  AnalyticsApiPicker,
  communicationModuleProperties,
  ConsolidatedFeature,
  ConsolidatorDevice,
  DataTrendingRequestBody,
  Feature,
  FeatureNames,
  RawTrendingData,
  RawTrendingDataSeries,
  trendingProperties,
} from '@vi-iot/client';
import {
  AppInsightsMonitoringService,
  LogLevelEnum,
} from 'src/app/shared/services/app-insights/app-insights-monitoring.service';
import { APP_INSIGHTS_DATA_TRENDING } from 'src/app/shared/services/app-insights/events/trending.events';
import { InstallationDetailsStore } from '../installation-details';

import { sortBy } from 'lodash';
import { DevicesStore, RichDevice } from '../devices';
import { TrendingActions } from './trending.actions';
import {
  EnergyStorageTrendingLabel,
  TrendingData,
  TrendingDataSeries,
  TrendingDataseriesErrorType,
  TrendingDateseriesError,
  TrendingEnumSeriesData,
  TrendingItem,
  TrendingTab,
  TrendingTypeEnum,
} from './trending.model';
import { TrendingStore } from './trending.store';
import { InstallationFeaturesStore } from '../installation-features';
import { DeviceTypes } from '../gateway';
import { TranslateService } from '@ngx-translate/core';
import { SUFFIX, Unit } from 'src/app/shared/common/units';
import { constantWord } from 'src/app/shared/common/enums';
import { ordinalSuffixes } from 'src/app/shared/common/constants';

@Injectable()
export class TrendingEffects {
  getDataSeries$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(TrendingActions.getDataSeries),
      concatLatestFrom(() => [
        this.devicesStore.trendingDevices$,
        this.installationDetailsStore.getSelectedGateway$.pipe(take(1)),
        this.installationDetailsStore.trendingFeaturesOnDevices$,
        this.trendingStore.dateRange$,
        this.trendingStore.resolution$,
        this.installationFeaturesStore.selectInstallationDeviceFeatureByDeviceType(
          FeatureNames.inverterPowerConsolidated,
          DeviceTypes.ElectricityStorage
        ),
        this.installationFeaturesStore.selectInstallationDeviceFeatureByDeviceType(
          FeatureNames.stateOfChargeConsolidated,
          DeviceTypes.ElectricityStorage
        ),
      ]),
      tap(() => {
        this.appInsights.logEvent(APP_INSIGHTS_DATA_TRENDING.GET_TRENDINGS, LogLevelEnum.DEBUG);
      }),
      switchMap(
        ([
          _action,
          trendingDevices,
          gateway,
          trendingFeatures,
          dateRange,
          resolution,
          inverterPowerConsolidated,
          stateOfChargeConsolidated,
        ]) => {
          const devices = structuredClone(trendingDevices);

          const deviceIds = devices.map((entry) => entry.id);
          const hasCommunicationModuleProperties = communicationModuleProperties.some((prop) =>
            trendingFeatures.includes(prop)
          );
          if (hasCommunicationModuleProperties && !deviceIds.includes('')) {
            deviceIds.unshift('');
          }

          const params: DataTrendingRequestBody = {
            gateway_id: gateway.serial,
            device_id: deviceIds,
            start_datetime: dateRange.start,
            end_datetime: dateRange.end,
            resolution,
            properties: trendingFeatures,
          };

          const cascadeTotal: RichDevice = {
            gatewaySerial: gateway.serial,
            id: constantWord.Total,
            shortDeviceName: this.translateService.instant(
              '+INSTALLATIONS.INSTALLATION.TRENDING.FEATURE.ENERGY_STORAGE.CASCADE_TOTAL'
            ),
            boilerSerial: constantWord.Total,
          } as RichDevice;

          devices.push(cascadeTotal);

          if (hasCommunicationModuleProperties) {
            const gatewayDevice: RichDevice = {
              gatewaySerial: gateway.serial,
              id: '',
              shortDeviceName: this.translateService.instant(
                '+INSTALLATIONS.INSTALLATION.TRENDING.TRENDING_TAB.COMMUNICATION_MODULE'
              ),
              boilerSerial: gateway.serial,
            } as RichDevice;

            devices.push(gatewayDevice);
          }

          return this.analyticsApiPicker
            .instance()
            .getTrendingData(params)
            .pipe(
              catchError((error) => {
                // If cascade and one device cannot return data
                if (params.device_id.length > 0) {
                  return of({
                    data: [
                      {
                        series: [],
                        device_id: params.device_id,
                        gateway_id: params.gateway_id,
                        resolution: params.resolution,
                      },
                    ] as RawTrendingData[],
                  });
                }

                return throwError(error);
              })
            )
            .pipe(
              map((responses) =>
                responses.data[0].device_id.map((entry) => {
                  const inverterPowerConsolidatedDevices: ConsolidatorDevice[] = (inverterPowerConsolidated as Feature<
                    ConsolidatedFeature,
                    unknown
                  >)?.properties?.devices?.value;
                  const stateOfChargeConsolidatedDevices: ConsolidatorDevice[] = (stateOfChargeConsolidated as Feature<
                    ConsolidatedFeature,
                    unknown
                  >)?.properties?.devices?.value;

                  const filteredEnumSeries = responses.data[0].enum_series?.filter(
                    (es) => es.device_id === entry
                  );

                  const filteredEnumProperties = {};
                  if (responses.data[0].enum_properties) {
                    filteredEnumSeries?.forEach((es) => {
                      const key = es.feature;
                      if (responses.data[0].enum_properties[key] !== undefined) {
                        filteredEnumProperties[key] = responses.data[0].enum_properties[key];
                      }
                    });
                  }

                  return {
                    datalength: responses.data[0].datalength,
                    enum_properties: filteredEnumProperties,
                    enum_series: filteredEnumSeries,
                    gateway_id: responses.data[0].gateway_id,
                    point_interval: responses.data[0].point_interval,
                    point_start: responses.data[0].point_start,
                    point_end: new Date(dateRange.end).getTime(),
                    resolution: responses.data[0].resolution,
                    device_id: [entry],
                    series: responses.data[0].series
                      .filter((series) => series.device_id === entry)
                      .map((data) => {
                        const memberId = data.feature.split('/')[1];

                        if (memberId === constantWord.Total) {
                          return {
                            ...data,
                            device_id: constantWord.Total,
                          };
                        }

                        switch (true) {
                          case data.feature.includes(FeatureNames.inverterPowerConsolidated):
                          case data.feature.includes(FeatureNames.powerConsolidated):
                            return this.getSeriesWithMemberId(
                              inverterPowerConsolidatedDevices,
                              memberId,
                              data
                            );
                          case data.feature.includes(FeatureNames.stateOfChargeConsolidated):
                            return this.getSeriesWithMemberId(
                              stateOfChargeConsolidatedDevices,
                              memberId,
                              data
                            );
                          default:
                            return {
                              ...data,
                            };
                        }
                      }),
                  };
                })
              ),
              map((trendingData: RawTrendingData[]) => {
                const rawTrendingTotalData: RawTrendingData = {
                  datalength: trendingData[0].datalength,
                  device_id: [constantWord.Total],
                  enum_properties: trendingData[0].enum_properties,
                  enum_series: trendingData[0].enum_series,
                  gateway_id: trendingData[0].gateway_id,
                  point_interval: trendingData[0].point_interval,
                  point_start: trendingData[0].point_start,
                  point_end: trendingData[0].point_end,
                  resolution: trendingData[0].resolution,
                  series: [],
                };
                const trendingDataWithTotal: RawTrendingData[] = JSON.parse(
                  JSON.stringify(trendingData)
                );
                trendingDataWithTotal.push(rawTrendingTotalData);

                const isEmptySeries = trendingDataWithTotal.every(
                  (entry) => entry.series.length === 0
                );
                if (isEmptySeries) {
                  return TrendingActions.getDataSeriesError({
                    error: TrendingDateseriesError.NoDataWithinRange,
                  });
                }

                const trendingDataWithSeries = this.restructureRawTrendingData(
                  trendingDataWithTotal
                ).filter((entry) => entry.series.length);

                const dataNormalized = this.normalizeTrendingData(trendingDataWithSeries, devices);

                return TrendingActions.getDataSeriesSuccess({
                  dataSeries: dataNormalized,
                });
              }),
              catchError((error) => {
                this.appInsights.logEvent(
                  APP_INSIGHTS_DATA_TRENDING.GET_TRENDINGS_ERROR,
                  LogLevelEnum.ERROR
                );

                const isDataNotFound502 =
                  error.statusCode === 502 &&
                  error.errorType === TrendingDataseriesErrorType.DataNotFound;
                const isBigDataError400 =
                  error.statusCode === 400 &&
                  error.errorType === TrendingDataseriesErrorType.BigDataError;
                const isConnectionTimeout504 =
                  error.statusCode === 504 &&
                  error.errorType === TrendingDataseriesErrorType.ConnectionTimeout;

                if (isDataNotFound502) {
                  return of(
                    TrendingActions.getDataSeriesError({
                      error: TrendingDateseriesError.ErrorDataNotFound502,
                    })
                  );
                } else if (isBigDataError400) {
                  return of(
                    TrendingActions.getDataSeriesError({
                      error: TrendingDateseriesError.ErrorBigDataError400,
                    })
                  );
                } else if (isConnectionTimeout504) {
                  return of(
                    TrendingActions.getDataSeriesError({
                      error: TrendingDateseriesError.ErrorConnectionTimeout504,
                    })
                  );
                } else {
                  return of(
                    TrendingActions.getDataSeriesError({ error: TrendingDateseriesError.Default })
                  );
                }
              })
            );
        }
      )
    );
  });

  parametersChanged$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(TrendingActions.setDateRange, TrendingActions.setResolution),
      map(() => TrendingActions.getDataSeries())
    );
  });

  getTrendingTabData$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(TrendingActions.selectTrendingTabData.attempt),
      concatLatestFrom(() => [
        this.devicesStore.selectFeatureForEnergyStorageDataTrending$,
        this.installationDetailsStore.trendingFeaturesOnDevices$,
      ]),
      switchMap(([_action, featureForEnergyStorageDataTrending, trendingFeaturesOnDevices]) => {
        const isShowHeatingTab = this.checkIsHasFeature(
          trendingProperties,
          trendingFeaturesOnDevices
        );
        const isShowEnergyStorageTab = this.checkIsHasFeature(
          featureForEnergyStorageDataTrending,
          trendingFeaturesOnDevices
        );

        const isShowCommunicationModuleTab = this.checkIsHasFeature(
          communicationModuleProperties,
          trendingFeaturesOnDevices
        );

        const trendingTabData: TrendingTab[] = [
          {
            name: this.translateService.instant(
              '+INSTALLATIONS.INSTALLATION.TRENDING.TRENDING_TAB.HEATING'
            ),
            type: TrendingTypeEnum.HEATING,
            isActive: isShowHeatingTab ? true : false,
            isShow: isShowHeatingTab,
          },
          {
            name: this.translateService.instant(
              '+INSTALLATIONS.INSTALLATION.TRENDING.TRENDING_TAB.ENERGY_STORAGE'
            ),
            type: TrendingTypeEnum.ENERGY_STORAGE,
            isActive: isShowHeatingTab ? false : true,
            isShow: isShowEnergyStorageTab,
          },
          {
            name: this.translateService.instant(
              '+INSTALLATIONS.INSTALLATION.TRENDING.TRENDING_TAB.COMMUNICATION_MODULE'
            ),
            type: TrendingTypeEnum.COMMUNICATION_MODULE,
            isActive: !isShowHeatingTab && !isShowEnergyStorageTab,
            isShow: isShowCommunicationModuleTab,
          },
        ];

        return of(TrendingActions.selectTrendingTabData.success({ trendingTabData }));
      })
    );
  });

  getEnergyStorageTrendingLabel$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(TrendingActions.getEnergyStorageLabels.attempt),
      concatLatestFrom(() => [
        this.devicesStore.selectBatteryWithMostValues$,
        this.devicesStore.selectVitoChargeDevices$,
      ]),
      switchMap(([_action, batteryWithMostValues, vitoChargeDevices]) => {
        const isIndividualVitocharge = vitoChargeDevices && vitoChargeDevices.length === 1;

        //BatteryModuleVoltageLabel
        const dlEssBatteryOperatingDataVoltageFeatures: TrendingItem[] = batteryWithMostValues.items.map(
          (_battery, index) => ({
            feature: FeatureNames.dlEssBatteryOperatingDataVoltage(index),
            label: this.translateService.instant(
              '+INSTALLATIONS.INSTALLATION.TRENDING.FEATURE.ENERGY_STORAGE.BATTERY_MODULE_LABEL',
              { index: index + 1 }
            ),
            visible: true,
            unit: Unit.Voltage,
          })
        );

        //PowerTrendingChartsLabel
        const powerTrendingChartsTotalLabel: TrendingItem[] = [
          {
            feature: isIndividualVitocharge
              ? FeatureNames.essInverterAcPowerActivePower
              : FeatureNames.dlEssInverterAcPowerConsolidatedTotal,
            label: this.translateService.instant(
              '+INSTALLATIONS.INSTALLATION.TRENDING.FEATURE.ENERGY_STORAGE.PHOTOVOLTAIC_POWER'
            ),
            visible: true,
            unit: Unit.Watt,
          },
          {
            feature: isIndividualVitocharge
              ? FeatureNames.essPowerValue
              : FeatureNames.dlEssPowerConsolidatedTotal,
            label: this.translateService.instant(
              '+INSTALLATIONS.INSTALLATION.TRENDING.FEATURE.ENERGY_STORAGE.POWER'
            ),
            visible: true,
            unit: Unit.Watt,
          },
        ];

        const dlEssInverterAcPowerConsolidatedFeatures: TrendingItem[] = vitoChargeDevices.map(
          (_device, index) => ({
            feature: FeatureNames.dlEssInverterAcPowerConsolidatedItem(index + 1),
            label: this.translateService.instant(
              '+INSTALLATIONS.INSTALLATION.TRENDING.FEATURE.ENERGY_STORAGE.PHOTOVOLTAIC_POWER'
            ),
            visible: false,
            unit: Unit.Watt,
          })
        );

        const dlEssPowerConsolidatedFeature: TrendingItem[] = vitoChargeDevices.map(
          (_device, index) => ({
            feature: FeatureNames.dlEssPowerConsolidatedItem(index + 1),
            label: this.translateService.instant(
              '+INSTALLATIONS.INSTALLATION.TRENDING.FEATURE.ENERGY_STORAGE.POWER'
            ),
            visible: false,
            unit: Unit.Watt,
          })
        );

        //StateOfChargeLabel
        const stateOfChargeConsolidatedTotalLabel: TrendingItem[] = [
          {
            feature: isIndividualVitocharge
              ? FeatureNames.essStateOfChargeValue
              : FeatureNames.dlEssStateOfChargeConsolidatedTotal,
            label: this.translateService.instant(
              '+INSTALLATIONS.INSTALLATION.TRENDING.FEATURE.ENERGY_STORAGE.TOTAL_LABEL'
            ),
            visible: true,
            unit: Unit.ShortPercent,
          },
        ];

        const dlEssBatteryInformationStateOfChargeFeatures: TrendingItem[] = batteryWithMostValues.items.map(
          (_battery, index) => ({
            feature: FeatureNames.dlEssBatteryInformationStateOfCharge(index),
            label: this.translateService.instant(
              '+INSTALLATIONS.INSTALLATION.TRENDING.FEATURE.ENERGY_STORAGE.BATTERY_MODULE_LABEL',
              { index: index + 1 }
            ),
            visible: isIndividualVitocharge ? true : false,
            unit: Unit.ShortPercent,
          })
        );

        const dlEssStateOfChargeConsolidated: TrendingItem[] = vitoChargeDevices.map(
          (_device, index) => ({
            feature: FeatureNames.dlEssStateOfChargeConsolidated(index + 1),
            label: this.translateService.instant(
              '+INSTALLATIONS.INSTALLATION.TRENDING.FEATURE.ENERGY_STORAGE.TOTAL_LABEL'
            ),
            visible: isIndividualVitocharge ? true : false,
            unit: Unit.ShortPercent,
          })
        );

        //TemperaturesLabel
        const dlEssSensorsTemperatureAmbientFeatures: TrendingItem[] = batteryWithMostValues.items.flatMap(
          (_battery, batteryIndex) =>
            Array.from({ length: 8 }, (_, sensorIndex) => ({
              feature: FeatureNames.essBatteryOperatingDataTemperatureSensorsInternal(
                batteryIndex,
                sensorIndex + 1
              ),
              label: this.translateService.instant(
                '+INSTALLATIONS.INSTALLATION.TRENDING.FEATURE.ENERGY_STORAGE.MODULE_SENSOR_LABEL',
                { index: batteryIndex + 1, ordinal: this.convertNumberToOrdinal(sensorIndex + 1) }
              ),
              visible: batteryIndex === 0 ? true : false,
              unit: SUFFIX.celsius,
            }))
        );
        dlEssSensorsTemperatureAmbientFeatures.unshift({
          feature: FeatureNames.dlEssSensorsTemperatureAmbient,
          label: this.translateService.instant(
            '+INSTALLATIONS.INSTALLATION.TRENDING.FEATURE.ENERGY_STORAGE.OUTSIDE_TEMPERATURE_LABEL'
          ),
          visible: true,
          unit: SUFFIX.celsius,
        });

        const energyStorageTrendingLabel: EnergyStorageTrendingLabel = {
          powerTrendingChartsLabel: [
            ...powerTrendingChartsTotalLabel,
            ...dlEssInverterAcPowerConsolidatedFeatures,
            ...dlEssPowerConsolidatedFeature,
          ],
          stateOfChargeLabel: [
            ...stateOfChargeConsolidatedTotalLabel,
            ...dlEssBatteryInformationStateOfChargeFeatures,
            ...dlEssStateOfChargeConsolidated,
          ],
          temperaturesLabel: dlEssSensorsTemperatureAmbientFeatures,
          batteryModuleVoltageLabel: dlEssBatteryOperatingDataVoltageFeatures,
        };

        return of(TrendingActions.getEnergyStorageLabels.success({ energyStorageTrendingLabel }));
      }),
      catchError((error) => of(TrendingActions.getEnergyStorageLabels.error({ error })))
    );
  });

  constructor(
    private actions$: Actions,
    private analyticsApiPicker: AnalyticsApiPicker,
    private installationDetailsStore: InstallationDetailsStore,
    private trendingStore: TrendingStore,
    private devicesStore: DevicesStore,
    private appInsights: AppInsightsMonitoringService,
    private installationFeaturesStore: InstallationFeaturesStore,
    private translateService: TranslateService
  ) {}

  private normalizeTrendingData(data: RawTrendingData[], devices: RichDevice[]): TrendingData {
    if (data.length === 2 && data.find((item) => item.device_id.includes(constantWord.Total))) {
      data = data.filter((item) => !item.device_id.includes(constantWord.Total));
    }

    const sortedData = sortBy(data, 'device_id');
    const mainDevice = sortedData.find((entry) => entry.device_id[0] === '0') || sortedData[0];

    const mappedSeries = sortedData.reduce((acc: TrendingDataSeries[], b: RawTrendingData) => {
      const device = devices.find((device) => {
        return b.device_id.includes(device.id);
      });

      const seriesMapped: TrendingDataSeries[] = b.series.map((entry: RawTrendingDataSeries) => {
        return {
          feature: entry.feature,
          boilerSerialFeature: `${device?.boilerSerial}_${entry.feature}`,
          data: entry.data,
          deviceId: device?.id,
          boilerSerial: device?.boilerSerial,
          deviceType: device?.deviceType,
          shortDeviceName: device?.shortDeviceName,
        };
      });

      return acc.concat(seriesMapped);
    }, []);

    const mappedEnumProperties = {};
    let mappedEnumSeries: TrendingEnumSeriesData[] = [];

    sortedData.forEach((entry) => {
      const device = devices.find((device) => {
        return entry.device_id.includes(device.id);
      });

      if (entry.enum_properties) {
        Object.keys(entry.enum_properties).map((enumMapping: string) => {
          const key = `${device.boilerSerial}_${enumMapping}`;

          mappedEnumProperties[key] = entry.enum_properties[enumMapping];
        });
        if (entry.enum_series) {
          mappedEnumSeries = mappedEnumSeries.concat(entry.enum_series);
        }
      }
    });

    const devicesMapped = sortedData.map((entry) => {
      const device = devices.find((device) => {
        return entry.device_id.includes(device.id);
      });

      return {
        deviceId: device.id,
        deviceType: device.deviceType,
        boilerSerial: device.boilerSerial,
        shortDeviceName: device?.shortDeviceName,
      };
    });

    const normalizedData: TrendingData = {
      point_interval: mainDevice.point_interval,
      point_start: mainDevice.point_start,
      point_end: mainDevice.point_end,
      enum_properties: mappedEnumProperties,
      gateway_id: mainDevice.gateway_id,
      resolution: mainDevice.resolution,
      devices: devicesMapped,
      series: sortBy(mappedSeries, 'boilerSerialFeature'),
      enum_series: mappedEnumSeries,
    };

    return normalizedData;
  }

  private getSeriesWithMemberId(
    devices: ConsolidatorDevice[],
    memberId: string,
    series: RawTrendingDataSeries
  ): RawTrendingDataSeries {
    if (!devices) {
      return series;
    }

    const selectedDevice = devices.find((item) => item.memberId === parseInt(memberId));

    return {
      ...series,
      device_id: selectedDevice ? selectedDevice.id : series.device_id,
    };
  }

  private restructureRawTrendingData(dataArray: RawTrendingData[]): RawTrendingData[] {
    const result: RawTrendingData[] = JSON.parse(JSON.stringify(dataArray));

    const seriesToMove: {
      series: RawTrendingDataSeries;
      fromIndex: number;
      toIndex: number;
    }[] = [];

    result.forEach((obj, objIndex) => {
      obj.series.forEach((seriesItem) => {
        if (obj.device_id.includes(seriesItem.device_id)) {
          return;
        }

        const targetObjIndex = result.findIndex((targetObj) =>
          targetObj.device_id.includes(seriesItem.device_id)
        );

        if (targetObjIndex !== -1 && targetObjIndex !== objIndex) {
          seriesToMove.push({
            series: seriesItem,
            fromIndex: objIndex,
            toIndex: targetObjIndex,
          });
        }
      });
    });

    seriesToMove.forEach(({ series, fromIndex, toIndex }) => {
      result[toIndex].series.push(series);

      const sourceSeriesIndex = result[fromIndex].series.findIndex(
        (item) => item.feature === series.feature && item.device_id === series.device_id
      );

      if (sourceSeriesIndex !== -1) {
        result[fromIndex].series.splice(sourceSeriesIndex, 1);
      }
    });

    return result;
  }

  private convertNumberToOrdinal(num: number): string {
    const lastTwoDigits: number = num % 100;

    return (
      num +
      (ordinalSuffixes[(lastTwoDigits - 20) % 10] ||
        ordinalSuffixes[lastTwoDigits] ||
        ordinalSuffixes[0])
    );
  }

  private checkIsHasFeature(
    trendingProperties: string[],
    trendingFeaturesOnDevices: string[]
  ): boolean {
    return trendingProperties.some((item) => trendingFeaturesOnDevices.includes(item));
  }
}
